import React, { FC, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography } from 'antd';

import { AuthState, getAuthState, forgottenPassword } from '../../store/actions/auth';
import { MainReducerState } from '../../store/reducers';

import Seo from '../../components/Seo';
import { getRoute, RoutePathName } from '../../routes';
import LoginLayout from '../../components/LoginLayout';
import { FormProps } from 'antd/lib/form';

interface ForgottenPasswordProps extends RouteComponentProps {
    resetForgottenPassword: typeof forgottenPassword.reset;
    sendForgottenPassword: typeof forgottenPassword.trigger;
    authState: AuthState;
}

const ForgottenPassword: FC<ForgottenPasswordProps> = ({ authState, resetForgottenPassword, sendForgottenPassword }) => {
    const [, setEmail] = useState<string | undefined>();
    const onFormValidSubmit: FormProps['onFinish'] = (values) => {
        setEmail(values.username);
        sendForgottenPassword(values);
    };
    const requiredRule = { required: true, message: 'Ce champ est obligatoire' };

    let error = authState.forgottenPasswordError ? 'Erreur' : null;

    if (
        authState.forgottenPasswordError &&
        authState.forgottenPasswordError.status &&
        authState.forgottenPasswordError.status === 404
    ) {
        error = 'Êtes-vous sûr d\'avoir saisi la bonne adresse e-mail ?';
    }

    useEffect(() => () => {
        setEmail(undefined);
        resetForgottenPassword();
    }, [setEmail, resetForgottenPassword]);

    return (
        <LoginLayout>
            <Seo title="Mot de passe oublié ?" />
            <Form
                className="login-form"
                onFinish={onFormValidSubmit}
                layout="vertical"
            >
                <img className="logo" src={`${process.env.PUBLIC_URL}/images/logo.jpg`} alt="logo" />
                <Typography.Title level={1}>
                    Mot de passe oublié ?
                </Typography.Title>

                {authState.forgottenPasswordSuccess ? (
                    <Typography.Paragraph>
                        Nous venons de vous envoyer un lien pour réinitialiser votre mot de passe à l’adresse email indiquée.
                        <br />Merci de surveiller vos e-mails.
                    </Typography.Paragraph>
                ) : (
                    <>
                        <Typography.Paragraph>
                            <p>
                                Saisissez l’adresse e-mail associé à votre compte, nous vous enverrons un mail avec un lien pour réinitialiser votre mot de passe
                            </p>
                        </Typography.Paragraph>
                        <Form.Item
                            name="username"
                            label="Email"
                            rules={[requiredRule, {
                                type: 'email',
                                message: 'Adresse e-mail invalide',
                            }]}
                            validateTrigger="onBlur"
                        >
                            <Input
                                size="large"
                                placeholder="Saisir une adresse email"
                            />
                        </Form.Item>
                        <Form.Item>
                            {error ? (
                                <div className="login-error-message">
                                    <Alert
                                        type="error"
                                        message={error}
                                        showIcon
                                    />
                                </div>
                            ) : null}

                            <Button
                                type="primary"
                                size="large"
                                shape="round"
                                htmlType="submit"
                                loading={authState.loading}
                                block
                            >
                                Réinitialiser votre mot de passe
                            </Button>
                        </Form.Item>
                    </>
                )}
                <Link to={getRoute(RoutePathName.login)}>
                    Revenir à l’écran de connexion
                </Link>
            </Form>
        </LoginLayout>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    authState: getAuthState(state),
});

export default connect(
    mapStateToProps,
    {
        sendForgottenPassword: forgottenPassword.trigger,
        resetForgottenPassword: forgottenPassword.reset,
    },
)(ForgottenPassword);
